import { MessageTemplateType } from "@tellescope/types-models";

export interface TemplatesFilters {
  type?: MessageTemplateType[];
  channels?: string[];
  roles?: string[];
  entities?: string[];
  userTags?: string[];
  tags?: string[];
  showArchived?: boolean;
}

export const TEMPLATE_TYPE_VALUES: MessageTemplateType[] = ["enduser", "Reply", "team"];

export const CHANNEL_VALUES = ["Email", "SMS", "Chat"] as const;

/**
 * Builds the `mdbFilter` passed to `templates.getSome` from the configured filters.
 * Each populated multi-select narrows by `$in`; archived templates are excluded
 * unless `showArchived` is set.
 */
export const buildTemplatesMdbFilter = (filters: TemplatesFilters): Record<string, unknown> => {
  const mdbFilter: Record<string, unknown> = {};

  if (filters.type?.length) mdbFilter.type = { $in: filters.type };
  if (filters.channels?.length) mdbFilter.forChannels = { $in: filters.channels };
  if (filters.roles?.length) mdbFilter.forRoles = { $in: filters.roles };
  if (filters.entities?.length) mdbFilter.forEntityTypes = { $in: filters.entities };
  if (filters.userTags?.length) mdbFilter["forUserTags.values"] = { $in: filters.userTags };
  if (filters.tags?.length) mdbFilter.tags = { $in: filters.tags };
  if (!filters.showArchived) mdbFilter.archivedAt = { $in: [null, ""] };

  return mdbFilter;
};

const toStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const strings = value.filter((entry): entry is string => typeof entry === "string");
  return strings.length ? strings : undefined;
};

/** Parses the generic `filter` field of a saved view back into typed `TemplatesFilters`. */
export const parseTemplatesFilters = (raw: Record<string, unknown> | undefined): TemplatesFilters => {
  if (!raw) return {};
  const types = toStringArray(raw.type)?.filter((value): value is MessageTemplateType =>
    TEMPLATE_TYPE_VALUES.some((known) => known === value)
  );
  return {
    type: types?.length ? types : undefined,
    channels: toStringArray(raw.channels),
    roles: toStringArray(raw.roles),
    entities: toStringArray(raw.entities),
    userTags: toStringArray(raw.userTags),
    tags: toStringArray(raw.tags),
    showArchived: raw.showArchived === true ? true : undefined,
  };
};

/** Counts the active filters for the toolbar badge (`showArchived` counts as one). */
export const countActiveTemplateFilters = (filters: TemplatesFilters): number => {
  const arrayKeys: (keyof TemplatesFilters)[] = ["type", "channels", "roles", "entities", "userTags", "tags"];
  return arrayKeys.reduce(
    (count, key) => {
      const value = filters[key];
      return Array.isArray(value) && value.length ? count + 1 : count;
    },
    filters.showArchived ? 1 : 0
  );
};
